/**
 * Primer uso — MOBILE: un paso por pantalla, a pantalla completa.
 *
 * Un paso por vez y nada más: en 390 px, mostrar cuatro secciones a la vez obliga a scrollear sin
 * saber cuánto falta. La barra de arriba dice en qué paso va y el pie dice para qué sirve el paso
 * — nadie debería tener que adivinar por qué la app le pide su altura.
 */

import { useState, type ReactNode } from 'react';
import { fechaLarga, type Sexo } from '@mg/core';

import { textoAlturaDesdeIMC } from '../features/useAjustes.ts';
import { useOnboarding } from '../features/useOnboarding.ts';
import { Aviso, Campo, Icono, Insignia, Pila, Selector, Vacio } from '../ui/componentes.tsx';
import type { PropsBienvenida } from './Bienvenida.tsx';

export function BienvenidaMobile({ alTerminar, onSalir }: PropsBienvenida): ReactNode {
  const o = useOnboarding(alTerminar);

  if (o.datos.cargando) return <Vacio>Cargando…</Vacio>;
  if (o.esperandoPlanilla) return <Preparando o={o} onSalir={onSalir} />;
  if (o.paso === 'listo') return <Listo o={o} />;

  const pasoActual = o.pasos[o.indice];

  return (
    <div className="m-app">
      <header className="m-cabecera" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 'var(--e2)' }}>
        <div className="fila fila--entre">
          <span className="eyebrow">
            Paso {o.indice + 1} de {o.pasos.length}
          </span>
          <div className="fila" style={{ gap: 'var(--e2)' }}>
            {o.paso !== 'perfil' && (
              <button type="button" className="boton boton--chico boton--fantasma" onClick={o.anterior}>
                <Icono nombre="atras" tamano={15} />
                Atrás
              </button>
            )}
            <button type="button" className="boton boton--chico boton--fantasma" onClick={() => void onSalir()}>
              Salir de la cuenta
            </button>
          </div>
        </div>
        {/* Tres estados y no dos: los pasos terminados van llenos, el que estás haciendo va a
            medias —está empezado, no hecho— y los que faltan, huecos. Con dos estados el paso
            actual se pintaba lleno y pegado al anterior: dos placas llenas contiguas se leen como
            una sola barra larga, y el dibujo decía "1 de 4" mientras el rótulo decía "Paso 2 de 4".
            El alto sube a 10 px porque la placa a medias no se distingue en 6. */}
        <Pila
          estados={o.pasos.map((_, i) => (i < o.indice ? 'llena' : i === o.indice ? 'parcial' : 'hueca'))}
          alto={10}
          etiqueta={`Paso ${o.indice + 1} de ${o.pasos.length}: ${o.indice} ${
            o.indice === 1 ? 'terminado' : 'terminados'
          } y este en curso`}
        />
      </header>

      <main className="m-cuerpo">
        <div>
          <h1 className="titulo" style={{ fontSize: 24 }}>
            {pasoActual?.titulo}
          </h1>
          <p className="nota" style={{ marginTop: 4 }}>
            {pasoActual?.para}
          </p>
        </div>

        {o.paso === 'perfil' && <PasoPerfil o={o} />}
        {o.paso === 'gimnasio' && <PasoGimnasio o={o} />}
        {o.paso === 'rutina' && <PasoRutina o={o} />}
        {o.paso === 'plan' && <PasoPlan o={o} />}

        {o.error && (
          <Aviso tono="atencion">
            {o.error}
            {o.puedeReintentar && (
              <button
                type="button"
                className="boton boton--chico"
                style={{ marginTop: 'var(--e2)' }}
                disabled={o.guardando}
                onClick={o.reintentar}
              >
                {o.guardando ? 'Subiendo…' : 'Reintentar'}
              </button>
            )}
          </Aviso>
        )}
      </main>

      <div style={{ padding: 'var(--e4)', paddingBottom: 'calc(var(--e4) + env(safe-area-inset-bottom))' }}>
        {o.queFalta && (
          <p className="nota--fina" style={{ marginBottom: 'var(--e2)', textAlign: 'center' }}>
            {o.queFalta}
          </p>
        )}
        <button
          type="button"
          className="boton boton--principal boton--registrar"
          disabled={!o.puedeSeguir || o.guardando}
          onClick={o.siguiente}
        >
          {o.guardando ? 'Guardando…' : o.paso === 'plan' ? 'Listo, empezar' : 'Seguir'}
        </button>
      </div>
    </div>
  );
}

/**
 * Con sesión pero sin planilla todavía. No es un spinner: es el único momento de la app en que
 * hace falta conexión sí o sí, así que lo dice y ofrece las dos salidas reales.
 */
function Preparando({
  o,
  onSalir,
}: {
  o: ReturnType<typeof useOnboarding>;
  onSalir: () => Promise<void>;
}): ReactNode {
  return (
    <div className="centrado">
      <div className="ancho-forma">
        <div>
          <h1 className="titulo" style={{ fontSize: 24 }}>
            Preparando tu planilla…
          </h1>
          <p className="nota" style={{ marginTop: 4 }}>
            Tus datos viven en una planilla tuya. Bajarla la primera vez —y en cada dispositivo
            nuevo— necesita conexión. Después la app funciona sin señal.
          </p>
        </div>

        {o.errorPlanilla && <Aviso tono="atencion">{o.errorPlanilla}</Aviso>}

        <button
          type="button"
          className="boton boton--principal"
          disabled={o.sincronizando}
          onClick={() => void o.reintentarPlanilla()}
        >
          {o.sincronizando ? 'Buscando…' : 'Reintentar'}
        </button>

        <button type="button" className="boton boton--fantasma boton--chico" onClick={() => void onSalir()}>
          Salir de la cuenta
        </button>
      </div>
    </div>
  );
}

/**
 * El último paso. Existe para no soltar a nadie en Inicio sin decirle dónde se registra la primera
 * serie: es la pregunta que todo el onboarding estuvo preparando, y hasta acá nadie la contestó.
 */
function Listo({ o }: { o: ReturnType<typeof useOnboarding> }): ReactNode {
  // Dos filas y no las tres del shell: acá no hay cabecera de paso que mostrar.
  return (
    <div className="m-app" style={{ gridTemplateRows: '1fr auto' }}>
      <main className="m-cuerpo" style={{ justifyContent: 'center' }}>
        <Pila estados={o.pasos.map(() => 'llena' as const)} alto={6} etiqueta="Los cuatro pasos, hechos" />
        <div>
          <h1 className="titulo" style={{ fontSize: 24 }}>
            Listo
          </h1>
          <p className="nota" style={{ marginTop: 4 }}>
            Tu gimnasio y tu rutina quedaron guardados. <strong>Tu primera serie se registra en
            Entrenar</strong>: abrís el día, tocás "Empezar la sesión" y el ＋ de cada ejercicio
            guarda una serie.
          </p>
        </div>
        <Aviso tono="info">Inicio te va a decir qué toca hoy. Nada de esto queda fijo: se cambia cuando quieras.</Aviso>
      </main>

      <div style={{ padding: 'var(--e4)', paddingBottom: 'calc(var(--e4) + env(safe-area-inset-bottom))' }}>
        <button type="button" className="boton boton--principal boton--registrar" onClick={o.cerrar}>
          Empezar
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function PasoPerfil({ o }: { o: ReturnType<typeof useOnboarding> }): ReactNode {
  return (
    <>
      <Campo etiqueta="Tu nombre" texto value={o.nombre} onChange={(e) => o.setNombre(e.target.value)} />

      <Campo
        etiqueta="Altura (cm)"
        inputMode="numeric"
        value={o.altura}
        onChange={(e) => o.setAltura(e.target.value)}
        ayuda="Se pide una sola vez. Si no la sabés, seguí y la completás después."
      />

      {o.alturaSugerida && (
        <Aviso tono="info">
          {textoAlturaDesdeIMC(o.alturaSugerida)}
          <button
            type="button"
            className="boton boton--chico"
            style={{ marginTop: 8 }}
            onClick={() => o.setAltura(String(o.alturaSugerida))}
          >
            Usar {o.alturaSugerida} cm
          </button>
        </Aviso>
      )}

      <Selector etiqueta="Sexo" value={o.sexo} onChange={(e) => o.setSexo(e.target.value as Sexo)}>
        <option value="M">Masculino</option>
        <option value="F">Femenino</option>
        <option value="otro">Prefiero no decirlo</option>
      </Selector>

      <div>
        <span className="eyebrow etiqueta">¿En qué unidad pensás el peso?</span>
        <div className="fila" style={{ gap: 'var(--e2)' }}>
          <button
            type="button"
            className={o.unidad === 'kg' ? 'boton boton--principal crece' : 'boton crece'}
            onClick={() => o.setUnidad('kg')}
          >
            Kilos
          </button>
          <button
            type="button"
            className={o.unidad === 'lb' ? 'boton boton--principal crece' : 'boton crece'}
            onClick={() => o.setUnidad('lb')}
          >
            Libras
          </button>
        </div>
        <p className="nota--fina" style={{ marginTop: 4 }}>
          Da igual si las máquinas están en la otra: la app guarda lo que leés en la placa y muestra
          las dos.
        </p>
      </div>
    </>
  );
}

function PasoGimnasio({ o }: { o: ReturnType<typeof useOnboarding> }): ReactNode {
  return (
    <>
      {o.gimnasiosDisponibles.length > 0 && (
        <div>
          <span className="eyebrow etiqueta">¿Es uno de estos?</span>
          <div className="columna" style={{ gap: 'var(--e2)' }}>
            {o.gimnasiosDisponibles.map((g) => (
              <button
                key={g.id}
                type="button"
                className={o.gimnasioExistente === g.id ? 'boton boton--principal' : 'boton'}
                onClick={() => o.setGimnasioExistente(o.gimnasioExistente === g.id ? null : g.id)}
                style={{ justifyContent: 'flex-start' }}
              >
                {g.nombre}
                {g.notas && <span className="nota--fina">· {g.notas}</span>}
              </button>
            ))}
          </div>
          <p className="nota--fina" style={{ marginTop: 6 }}>
            Si elegís uno existente heredás su mapa de máquinas: dónde está cada aparato.
          </p>
        </div>
      )}

      {o.gimnasioExistente === null && (
        <>
          <Campo
            etiqueta="Nombre del gimnasio"
            texto
            placeholder="Ej. Smart Fit Providencia"
            value={o.nombreGimnasio}
            onChange={(e) => o.setNombreGimnasio(e.target.value)}
          />

          <div>
            <span className="eyebrow etiqueta">¿Qué días cierra?</span>
            {/* Siete columnas fijas y no `flex-wrap`: con wrap, el domingo —que viene marcado, o
                sea ámbar— caía solo a una segunda fila y a lo ancho, y un botón ámbar solo en su
                fila se lee como la acción principal de la pantalla, no como un día de la semana.
                En una grilla de semana ninguno puede parecer otra cosa. */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 6 }}>
              {o.diasSemana.map((d) => (
                <button
                  key={d.n}
                  type="button"
                  className={
                    o.diasCierre.includes(d.n) ? 'boton boton--chico boton--principal' : 'boton boton--chico'
                  }
                  aria-pressed={o.diasCierre.includes(d.n)}
                  onClick={() => o.alternarDia(d.n)}
                  style={{ padding: 0, minWidth: 0, minHeight: 48 }}
                >
                  {d.etiqueta}
                </button>
              ))}
            </div>
            <p className="nota--fina" style={{ marginTop: 6 }}>
              Marcá los días que cierra: no cuentan como días disponibles para tu plan. Si no cierra
              ninguno, dejá todos sin marcar.
            </p>
          </div>
        </>
      )}
    </>
  );
}

function PasoRutina({ o }: { o: ReturnType<typeof useOnboarding> }): ReactNode {
  const [editando, setEditando] = useState<number | null>(null);
  // Quitar un día se pregunta antes: es lo único de este paso que borra algo que el usuario ya
  // armó, y no hay deshacer.
  const [confirmando, setConfirmando] = useState<number | null>(null);

  if (o.plantillaId === null && o.dias.length === 0) {
    return (
      <>
        <div className="columna" style={{ gap: 'var(--e3)' }}>
          {o.plantillas.map((p) => (
            <button
              key={p.id}
              type="button"
              className="tarjeta"
              onClick={() => o.elegirPlantilla(p)}
              style={{ textAlign: 'left', color: 'inherit', cursor: 'pointer' }}
            >
              <div className="fila fila--entre">
                <strong>{p.nombre}</strong>
                <Insignia>
                  {p.dias.length} {p.dias.length === 1 ? 'día' : 'días'}
                </Insignia>
              </div>
              <p className="nota--fina" style={{ marginTop: 4 }}>
                {p.descripcion}
              </p>
            </button>
          ))}
        </div>

        <button type="button" className="boton boton--fantasma" onClick={() => o.elegirPlantilla(null)}>
          Armarla desde cero
        </button>

        <p className="nota--fina">
          Cualquiera de estas se puede editar ahora y después. La app no prescribe rutinas: sólo te
          evita la pantalla en blanco.
        </p>
      </>
    );
  }

  return (
    <>
      <div className="fila fila--entre">
        <span className="eyebrow">Tu rutina</span>
        <button type="button" className="boton boton--chico boton--fantasma" onClick={() => o.elegirPlantilla(null)}>
          Cambiar plantilla
        </button>
      </div>

      {o.dias.map((d, i) => (
        <div key={i} className="tarjeta">
          {/* El nombre, a lo ancho de la tarjeta. Antes compartía la fila con la × de quitar el
              día: "Día 2 — hombros, espalda alta y core" entraba a la mitad y se cortaba sin
              puntos suspensivos, así que no se veía que estaba cortado. */}
          <input
            className="campo campo--texto"
            style={{ minHeight: 44, fontWeight: 600, textOverflow: 'ellipsis', marginBottom: 'var(--e2)' }}
            value={d.nombre}
            onChange={(e) => o.renombrarDia(i, e.target.value)}
            aria-label={`Nombre del día ${i + 1}`}
            title={d.nombre}
          />

          <div className="columna" style={{ gap: 4 }}>
            {d.ejercicios.map((id) => (
              <div key={id} className="fila fila--entre">
                <span style={{ fontSize: 14 }}>{o.catalogoPorId.get(id)?.nombre ?? id}</span>
                <button
                  type="button"
                  className="boton boton--chico boton--fantasma"
                  style={{ minHeight: 32, padding: '0 8px' }}
                  onClick={() => o.quitarEjercicio(i, id)}
                  aria-label={`Quitar ${o.catalogoPorId.get(id)?.nombre ?? id}`}
                >
                  <Icono nombre="cerrar" tamano={13} />
                </button>
              </div>
            ))}
            {d.ejercicios.length === 0 && (
              <p className="nota--fina" style={{ color: 'var(--ambar)' }}>
                Este día quedó vacío. Agregale al menos un ejercicio.
              </p>
            )}
          </div>

          {editando === i ? (
            <div style={{ marginTop: 'var(--e3)' }}>
              <Selector
                etiqueta="Agregar ejercicio"
                value=""
                onChange={(e) => {
                  if (e.target.value) o.agregarEjercicio(i, e.target.value);
                }}
              >
                <option value="">Elegí uno…</option>
                {o.catalogo
                  .filter((e) => !d.ejercicios.includes(e.id))
                  .map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.nombre}
                    </option>
                  ))}
              </Selector>
              <button
                type="button"
                className="boton boton--chico boton--fantasma"
                style={{ marginTop: 'var(--e2)' }}
                onClick={() => setEditando(null)}
              >
                Listo
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="boton boton--chico boton--fantasma"
              style={{ marginTop: 'var(--e3)' }}
              onClick={() => setEditando(i)}
            >
              <Icono nombre="mas" tamano={14} />
              Agregar ejercicio
            </button>
          )}
        </div>
      ))}

      <button type="button" className="boton boton--fantasma" onClick={o.agregarDia}>
        <Icono nombre="mas" tamano={16} />
        Agregar un día
      </button>

      <div className="tarjeta">
        <span className="eyebrow">Series y reps por ejercicio</span>
        <div className="fila" style={{ gap: 'var(--e2)', marginTop: 'var(--e2)' }}>
          <div className="crece">
            <Campo
              etiqueta="Series"
              inputMode="numeric"
              value={String(o.seriesObjetivo)}
              onChange={(e) => o.setSeriesObjetivo(Math.max(1, Number(e.target.value) || 1))}
            />
          </div>
          <div className="crece">
            <Campo
              etiqueta="Reps mín"
              inputMode="numeric"
              value={String(o.repsMin)}
              onChange={(e) => o.setRepsMin(Math.max(1, Number(e.target.value) || 1))}
            />
          </div>
          <div className="crece">
            <Campo
              etiqueta="Reps máx"
              inputMode="numeric"
              value={String(o.repsMax)}
              onChange={(e) => o.setRepsMax(Math.max(o.repsMin, Number(e.target.value) || o.repsMin))}
            />
          </div>
        </div>
        <p className="nota--fina" style={{ marginTop: 'var(--e2)' }}>
          El rango es lo que la app usa para sugerirte cuándo subir la placa: cuando cerrás todas
          las series en el tope, subís un escalón.
        </p>
      </div>
    </>
  );
}

function PasoPlan({ o }: { o: ReturnType<typeof useOnboarding> }): ReactNode {
  return (
    <>
      <div className="fila" style={{ gap: 'var(--e2)' }}>
        <button
          type="button"
          className={o.conPlan ? 'boton boton--principal crece' : 'boton crece'}
          onClick={() => o.setConPlan(true)}
        >
          Compré sesiones
        </button>
        <button
          type="button"
          className={!o.conPlan ? 'boton boton--principal crece' : 'boton crece'}
          onClick={() => o.setConPlan(false)}
        >
          Voy libre
        </button>
      </div>

      {o.conPlan ? (
        <>
          <Campo
            etiqueta="¿Cuántas sesiones?"
            inputMode="numeric"
            value={o.sesionesTotales}
            onChange={(e) => o.setSesionesTotales(e.target.value)}
          />
          <Campo
            etiqueta="¿Cuándo vencen?"
            texto
            type="date"
            value={o.vencimiento}
            min={o.datos.hoy}
            onChange={(e) => o.setVencimiento(e.target.value)}
            ayuda={o.vencimiento > o.datos.hoy ? `Vence el ${fechaLarga(o.vencimiento)}.` : undefined}
          />
          <Aviso tono="info">
            Con esto la app calcula cuántos <strong>días de colchón</strong> te quedan y te avisa si
            no te va a alcanzar el tiempo. Los días que cierra el gimnasio y los viajes salen de la
            cuenta.
          </Aviso>
        </>
      ) : (
        <p className="nota">
          Sin plan la app registra todo igual. Podés cargar uno después, cuando compres un paquete.
        </p>
      )}
    </>
  );
}
